import React, { useEffect, useState, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import useActionStore from '../../store/Actions';
import useInsightsStore from '../../store/Insights';
import useChatsStore from '../../store/Chats';
import emptyChat from '@/assets/gifs/emptyChat.gif';
import {
  deleteAssistantThread,
  fetchAssistantThreads,
  loadThreadIntoStore,
} from '../../services/AssistantService';
import type { AssistantThread } from '../../types';
import ConfirmDelete from '@/components/modals/ConfirmDelete';
import PaginationControls from '@/components/ui/PaginationControls/PaginationControls';
import { formatTimestamp } from '@/utils/helpers/helpers';
import Spinner from '@/components/ui/Loading/Spinner';

export default function ChatsSidebar() {
  const { chatCollapsed, setChatCollapsed } = useActionStore();
  const { selectedChatId } = useChatsStore();
  const [threads, setThreads] = useState<AssistantThread[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showChatIcon, setShowChatIcon] = useState(chatCollapsed);

  useEffect(() => {
    if (chatCollapsed) {
      const timer = setTimeout(() => setShowChatIcon(true), 300);
      return () => clearTimeout(timer);
    } else {
      setShowChatIcon(false);
    }
  }, [chatCollapsed]);

  const loadThreads = useCallback(async (p: number) => {
    setLoading(true);
    try {
      const res = await fetchAssistantThreads(p);
      setThreads(res.data ?? []);
      if (res.meta?.totalPages) setTotalPages(res.meta.totalPages);
    } catch {
      // silently fail — sidebar is non-critical
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!chatCollapsed) {
      loadThreads(page);
    }
  }, [chatCollapsed, page, loadThreads]);

  useEffect(() => {
    if (chatCollapsed) return;
    if (!selectedChatId) return;
    if (threads.some((t) => t._id === selectedChatId)) return;
    if (page !== 1) {
      setPage(1);
    } else {
      loadThreads(1);
    }
  }, [selectedChatId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleCollapse = () => setChatCollapsed(!chatCollapsed);

  return (
    <AnimatePresence>
      <nav className="h-full w-full flex flex-col pt-[10px] pb-[20px] bg-frame-inner border-l border-navigation-stroke">
        {/* ── Collapse toggle ── */}
        <div className={`flex ${chatCollapsed ? 'self-center' : 'self-start'}`}>
          {chatCollapsed && showChatIcon ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={handleToggleCollapse}
              className="w-[25px] cursor-pointer py-2 items-center justify-center flex hover:opacity-80"
            >
              <ChatIcon />
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 1, scale: 1 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
              onClick={handleToggleCollapse}
              className={`w-[50px] py-2 items-center justify-center flex hover:opacity-80 transition-all duration-300 ease-in-out ${chatCollapsed ? 'rotate-180' : 'rotate-0'}`}
            >
              <Collapse />
            </motion.div>
          )}
        </div>

        {/* ── Expanded content ── */}
        {!chatCollapsed && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="px-4 mt-2 flex flex-col h-full min-h-0"
          >
            {/* Header */}
            <motion.header
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="mb-4 border-b-1 border-gray-2/40 pb-4">
                <h2 className="text-base font-bold text-text-primary-heading mb-1">
                  Chat History
                </h2>
                <p className="text-xs font-normal text-text-primary-body">
                  Easily get back to your conversations.
                </p>
              </div>
            </motion.header>

            {/* Thread list */}
            <div className="flex-1 min-h-0 overflow-y-auto pb-4 space-y-3">
              {loading ? (
                <ThreadSkeleton />
              ) : threads.length === 0 ? (
                <EmptyState />
              ) : (
                <>
                  {threads.map((thread) => (
                    <BackendThreadItem
                      key={thread._id}
                      thread={thread}
                      isActive={selectedChatId === thread._id}
                      onDeleted={(id) => {
                        setThreads((prev) => prev.filter((t) => t._id !== id));
                      }}
                    />
                  ))}
                </>
              )}
            </div>

            {/* Pagination */}
            {!loading && threads.length > 0 && (
              <PaginationControls
                currentPage={page}
                totalPages={totalPages}
                hasPrevious={page > 1}
                hasNext={page < totalPages}
                onPageChange={setPage}
                showingFrom={(page - 1) * threads.length + 1}
                showingTo={page * threads.length}
                totalItems={totalPages * threads.length}
                itemLabel="chats"
                className="mt-auto pt-3 border-t border-gray-2/30"
              />
            )}
          </motion.div>
        )}
      </nav>
    </AnimatePresence>
  );
}

// ── Backend thread item ────────────────────────────────────────────────────────

function BackendThreadItem({
  thread,
  isActive,
  onDeleted,
}: {
  thread: AssistantThread;
  isActive: boolean;
  onDeleted: (threadId: string) => void;
}) {
  const { updateSelectedChatId, deleteChat } = useChatsStore();
  const [loadingThread, setLoadingThread] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleClick = async () => {
    if (isActive) return;
    setHasError(false);
    updateSelectedChatId(thread._id);
    setLoadingThread(true);
    try {
      await loadThreadIntoStore(thread._id, thread.project_code_names ?? []);
    } catch (error) {
      console.error('[ChatsSidebar] Failed to load thread:', error);
      setHasError(true);
    } finally {
      setLoadingThread(false);
    }
  };

  const handleDelete = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await deleteAssistantThread(thread._id);
      deleteChat(thread._id);
      setConfirmOpen(false);
      onDeleted(thread._id);
    } catch (error) {
      console.error('[ChatsSidebar] Failed to delete thread:', error);
      setDeleteError('Could not delete chat. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div
      className={`group relative rounded-md p-3 flex flex-col w-full bg-[#121212] transition-all duration-200 ${
        isActive
          ? 'border border-gray-3 cursor-default'
          : 'cursor-pointer hover:bg-frame-stroke/80'
      } ${loadingThread ? 'opacity-60' : ''}`}
      onClick={handleClick}
    >
      <button
        type="button"
        aria-label="Delete chat"
        onClick={(e) => {
          e.stopPropagation();
          setConfirmOpen(true);
        }}
        className="absolute top-2 right-2 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-black-1/30 text-text-primary-body/60 hover:text-error-2 transition-all"
      >
        <TrashIcon />
      </button>

      <p className="text-sm font-normal text-text-primary-body line-clamp-2 break-words mb-2 pr-6">
        {thread.message_preview || 'No preview available'}
      </p>
      {thread.project_code_names && thread.project_code_names.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {thread.project_code_names.map((code) => (
            <span
              key={code}
              className="text-xs px-1.5 py-0.5 rounded-sm bg-navigation-tab-active text-text-primary-highlight"
            >
              {code}
            </span>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between mt-auto">
        <span className="text-[10px] text-text-primary-body/50">
          {formatTimestamp(thread.last_message_at)}
        </span>
        {loadingThread && (
          <div className="w-4 h-4 text-text-primary-body/50">
            <Spinner />
          </div>
        )}
      </div>

      {hasError && (
        <div className="mt-2 flex items-center justify-between rounded-md bg-red-500/10 px-2 py-1.5">
          <span className="text-[10px] text-red-400">Failed to load</span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
            className="text-[10px] text-red-400 hover:text-red-300 underline underline-offset-2 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {deleteError && !confirmOpen && (
        <div className="mt-2 rounded-md bg-red-500/10 px-2 py-1.5">
          <span className="text-[10px] text-red-400">{deleteError}</span>
        </div>
      )}

      {confirmOpen && (
        <div onClick={(e) => e.stopPropagation()}>
          <ConfirmDelete
            type="chat"
            isDeleting={isDeleting}
            onDelete={handleDelete}
            onClose={() => {
              if (isDeleting) return;
              setConfirmOpen(false);
              setDeleteError(null);
            }}
          />
        </div>
      )}
    </div>
  );
}

function TrashIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14zM10 11v6M14 11v6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ThreadSkeleton() {
  return (
    <>
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="rounded-md p-3 bg-frame-stroke animate-pulse">
          <div className="h-3 bg-gray-2/30 rounded w-full mb-2" />
          <div className="h-3 bg-gray-2/30 rounded w-3/4 mb-3" />
          <div className="h-2 bg-gray-2/20 rounded w-1/4 ml-auto" />
        </div>
      ))}
    </>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center h-[calc(65vh-100px)]">
      <img
        src={emptyChat}
        width={45}
        className="opacity-10"
        loading="lazy"
        alt=""
      />
      <p className="text-text-primary-body text-sm text-center mt-2">
        Your chats will appear here. Start chatting with Crafter to get insights
        on your project.
      </p>
    </div>
  );
}

function Collapse() {
  return (
    <svg
      className="cursor-pointer"
      width="22"
      height="17"
      viewBox="0 0 22 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6.11523 13.7707L10.4619 9.42403C10.9752 8.91069 10.9752 8.07069 10.4619 7.55736L6.11523 3.21069"
        stroke="#939393"
        strokeWidth="1.5"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.1152 13.7707L15.4619 9.42403C15.9752 8.91069 15.9752 8.07069 15.4619 7.55736L11.1152 3.21069"
        stroke="#939393"
        strokeWidth="1.5"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M13.3333 18.9582H6.66667C3.625 18.9582 1.875 17.2082 1.875 14.1665V5.83317C1.875 2.7915 3.625 1.0415 6.66667 1.0415H13.3333C16.375 1.0415 18.125 2.7915 18.125 5.83317V14.1665C18.125 17.2082 16.375 18.9582 13.3333 18.9582ZM6.66667 2.2915C4.28333 2.2915 3.125 3.44984 3.125 5.83317V14.1665C3.125 16.5498 4.28333 17.7082 6.66667 17.7082H13.3333C15.7167 17.7082 16.875 16.5498 16.875 14.1665V5.83317C16.875 3.44984 15.7167 2.2915 13.3333 2.2915H6.66667Z"
        fill="#888888"
      />
      <path
        d="M15.4163 7.70833H13.7497C12.483 7.70833 11.458 6.68333 11.458 5.41667V3.75C11.458 3.40833 11.7413 3.125 12.083 3.125C12.4247 3.125 12.708 3.40833 12.708 3.75V5.41667C12.708 5.99167 13.1747 6.45833 13.7497 6.45833H15.4163C15.758 6.45833 16.0413 6.74167 16.0413 7.08333C16.0413 7.425 15.758 7.70833 15.4163 7.70833Z"
        fill="#888888"
      />
      <path
        d="M10.0003 11.4585H6.66699C6.32533 11.4585 6.04199 11.1752 6.04199 10.8335C6.04199 10.4918 6.32533 10.2085 6.66699 10.2085H10.0003C10.342 10.2085 10.6253 10.4918 10.6253 10.8335C10.6253 11.1752 10.342 11.4585 10.0003 11.4585Z"
        fill="#7753FD"
      />
      <path
        d="M13.3337 14.7915H6.66699C6.32533 14.7915 6.04199 14.5082 6.04199 14.1665C6.04199 13.8248 6.32533 13.5415 6.66699 13.5415H13.3337C13.6753 13.5415 13.9587 13.8248 13.9587 14.1665C13.9587 14.5082 13.6753 14.7915 13.3337 14.7915Z"
        fill="#7753FD"
      />
    </svg>
  );
}
